// Condition suggestions for a token's conditions field (issue 855): the names
// of the site's conditions list for the table's system, asked of THIS origin
// (the server reads the site; the browser never does). Once per table per
// page; no list, or any failure, is no suggestions — free text always works.
// Issue 1585: the same answer names each condition's entry (its family and
// slug), so the Compendium's condition rows can open and preview it.
import { useEffect, useState } from 'react';
import { requestJson } from './api';

/** A condition's entry on the site: its name as the list names it, the family it lives at, and its slug. */
export interface ConditionEntry { name: string; segment: string; slug: string }
interface Answer { names: string[]; entries: ConditionEntry[] }

const ADDRESS = /^[a-z0-9][a-z0-9._-]{0,199}$/i;
const isEntry = (v: unknown): v is ConditionEntry => {
  const o = v as Record<string, unknown> | null;
  return !!o && typeof o.name === 'string' && typeof o.segment === 'string' && ADDRESS.test(o.segment) && typeof o.slug === 'string' && ADDRESS.test(o.slug);
};

const asked = new Map<string, Promise<Answer>>();

function ask(campaignId: string, fetchImpl: typeof fetch): Promise<Answer> {
  const have = asked.get(campaignId);
  if (have) return have;
  const asking = requestJson(`/api/tables/${encodeURIComponent(campaignId)}/conditions`, {}, fetchImpl).then(({ status, body }) => {
    const b = body as { conditions?: unknown; entries?: unknown } | null;
    if (status !== 200 || !Array.isArray(b?.conditions)) { asked.delete(campaignId); return { names: [], entries: [] }; }
    return {
      names: b.conditions.filter((n): n is string => typeof n === 'string'),
      entries: Array.isArray(b.entries) ? b.entries.filter(isEntry) : [],
    };
  }).catch(() => { asked.delete(campaignId); return { names: [], entries: [] }; });
  asked.set(campaignId, asking);
  return asking;
}

export function conditionSuggestions(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<string[]> {
  return ask(campaignId, fetchImpl).then((a) => a.names);
}

/** Each condition's entry (issue 1585), from the same one read as the suggestions; none on any failure. */
export function conditionEntries(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<ConditionEntry[]> {
  return ask(campaignId, fetchImpl).then((a) => a.entries);
}

/** Forget what was asked (tests). */
export const forgetConditionSuggestions = () => asked.clear();

export function useConditionSuggestions(campaignId: string, enabled = true): string[] {
  const [names, setNames] = useState<string[]>([]);
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    void conditionSuggestions(campaignId).then((n) => { if (live) setNames(n); });
    return () => { live = false; };
  }, [campaignId, enabled]);
  return names;
}
